export type ImportOperation = {
  operationId: string;
  documentId?: string;
  type: string;
  targetPageId?: string;
  targetPath?: string[];
  payload?: Record<string, any>;
  confidence?: { placement?: number; duplicate?: number | null };
  reviewRequired?: boolean;
};

export const IMPORT_FLOW_STEPS = [
  { label: 'Documents', description: 'Choose files' },
  { label: 'Preferences', description: 'Set outcomes' },
  { label: 'Review plan', description: 'Check locations' },
  { label: 'Results', description: 'See what changed' },
] as const;

export const DESTINATION_EDITABLE_OPERATION_TYPES = new Set([
  'create_content_tile',
  'create_form_tile',
  'create_external_link_tile',
  'create_internal_link_tile',
]);

export function isDestinationEditable(operation: Pick<ImportOperation, 'type'>) {
  return DESTINATION_EDITABLE_OPERATION_TYPES.has(operation.type);
}

export function operationLabel(type: string) {
  const labels: Record<string, string> = {
    create_content_tile: 'Editable content tile',
    create_form_tile: 'Form tile',
    create_external_link_tile: 'Link to the original',
    create_internal_link_tile: 'Link to Nexus content',
    create_nested_page: 'New nested page',
    create_column_category: 'New page section',
    send_to_knowledge_inbox: 'Send to Knowledge Inbox',
    skip_document: 'Skip likely duplicate',
    document_parse_failed: 'Could not read document',
  };
  return labels[type] || type.replace(/_/g, ' ');
}

export function confidenceLabel(score?: number) {
  if (typeof score !== 'number') return 'Not scored';
  if (score >= 0.8) return 'High confidence';
  if (score >= 0.55) return 'Medium confidence';
  return 'Low confidence';
}

export function activeImportPhase(step: number) {
  if (step <= 1) return 1;
  if (step === 2) return 2;
  if (step <= 4) return 3;
  return 4;
}

export function storageModeAvailableForSources(
  storageMode: 'replicate' | 'external_link' | 'per_item',
  sourceTypes: Array<'upload' | 'external_link'>,
) {
  if (!sourceTypes.length) return false;
  if (storageMode === 'replicate') return true;
  return sourceTypes.some((type) => type === 'external_link');
}

export function resolveSourceHandling(
  storageMode: 'replicate' | 'external_link' | 'per_item',
  source: { sourceType: 'upload' | 'external_link'; handlingMode?: 'replicate' | 'external_link' },
) {
  if (source.sourceType === 'upload') return 'replicate' as const;
  if (storageMode === 'per_item') return source.handlingMode || 'external_link';
  return storageMode;
}
